/**
 * Sprint 23.7 — `<StarterChip>`: one of the four "or start from" tiles in the
 * empty Workforce state. A ramp-tinted icon circle + a title + a one-line
 * description. Clicking pre-fills the Hire wizard with the chip's intent string
 * (the three concrete starters) or opens it blank ("Describe your own").
 */

import { type CSSProperties } from "react";

export type StarterRamp = "purple" | "teal" | "coral" | "neutral";

export interface StarterChipProps {
  /** Tabler icon name without the `ti-` prefix. */
  icon: string;
  label: string;
  description: string;
  ramp?: StarterRamp;
  /** "Describe your own" gets a dashed outer border. */
  dashed?: boolean;
  onClick: () => void;
}

const RAMP_TINT: Record<StarterRamp, { bg: string; fg: string }> = {
  purple: { bg: "var(--c-purple-50)", fg: "var(--c-purple-600)" },
  teal: { bg: "var(--c-teal-50)", fg: "var(--c-teal-600)" },
  coral: { bg: "var(--c-coral-50)", fg: "var(--c-coral-600)" },
  neutral: { bg: "var(--color-background-secondary)", fg: "var(--color-text-tertiary)" },
};

export function StarterChip({ icon, label, description, ramp = "neutral", dashed = false, onClick }: StarterChipProps) {
  const tint = RAMP_TINT[ramp];

  const style: CSSProperties = {
    padding: "10px 12px",
    fontSize: 12,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    background: "var(--color-background-primary)",
    border: dashed
      ? "0.5px dashed var(--color-border-tertiary)"
      : "0.5px solid var(--color-border-tertiary)",
    borderRadius: "var(--border-radius-md)",
    textAlign: "left",
    cursor: "pointer",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
  };

  return (
    <button type="button" onClick={onClick} style={style}>
      <span
        style={{
          width: 26,
          height: 26,
          borderRadius: "50%",
          background: tint.bg,
          color: tint.fg,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <i className={`ti ti-${icon}`} style={{ fontSize: 13 }} aria-hidden="true" />
      </span>
      <span style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: "var(--color-text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {description}
        </span>
      </span>
    </button>
  );
}
